import React, { useMemo } from "react";
import { formatDateVN } from "../../../utils/formatUtils";
import { numberToVietnameseText } from "../../../utils/vietnameseNumberWords";

export const paymentMethodLabel = (method) => {
  const m = String(method || "").toUpperCase();
  if (m === "CASH") return "Tiền mặt";
  if (m === "BANK_TRANSFER") return "Chuyển khoản";
  return method || "—";
};

export const STATUS_MAP = {
  draft: { label: "Nháp", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  waiting: { label: "Chờ duyệt", cls: "bg-amber-50 text-amber-700 border-amber-100" },
  approved: { label: "Đã duyệt", cls: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  rejected: { label: "Từ chối", cls: "bg-red-50 text-red-700 border-red-100" },
  completed: { label: "Đã quyết toán", cls: "bg-blue-50 text-blue-700 border-blue-100" },
};

const DEFAULT_APPROVER_SLOTS = [
  { role_key: "hc_kt", title: "HC/KT" },
  { role_key: "coo", title: "Giám đốc điều hành" },
  { role_key: "ceo", title: "Tổng giám đốc" },
];

const stripEnglishRoleSuffix = (title) =>
  String(title || "")
    .replace(/\s*\((?:COO|CEO)\)\s*/gi, "")
    .trim();

const amountToWords = (amount) => {
  const text = numberToVietnameseText(Number(amount || 0));
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const formatAmountInWords = amountToWords;

const DocRow = ({ label, value, mono }) => (
  <div className="flex gap-2">
    <span className="shrink-0 text-slate-500 min-w-[148px]">- {label}:</span>
    <span className={`font-medium text-slate-800 break-words ${mono ? "font-mono" : ""}`}>
      {value || "…………………………"}
    </span>
  </div>
);

/** Timestamp kiểu HĐĐT: HH:mm:ss dd/MM/yyyy */
const formatSignedAt = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    const time = d.toLocaleTimeString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const date = d.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    return `${time} ${date}`;
  } catch {
    return "";
  }
};

/** Số hiệu chữ ký số / mã quản lý — định dạng kiểu HĐĐT từ UUID bản ghi */
export const formatSignatureSerial = (recordId, documentCode = "") => {
  if (!recordId) return null;
  const compact = String(recordId).replace(/-/g, "").toUpperCase();
  if (compact.length < 16) return null;
  const docPrefix = String(documentCode || "IMA")
    .replace(/[^A-Za-z0-9]/g, "")
    .slice(0, 6)
    .toUpperCase() || "IMA";
  return `${docPrefix}-${compact.slice(0, 8)}-${compact.slice(8, 16)}-${compact.slice(16, 24)}`;
};

const ESignValidIcon = () => (
  <svg
    className="h-3 w-3 shrink-0"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="7" stroke="#059669" strokeWidth="1.25" />
    <path
      d="M5 8l2 2 4-4"
      stroke="#059669"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Xây tối thiểu 4 ô ký điện tử: Người đề nghị + HC/KT + Giám đốc điều hành + Tổng giám đốc.
 * Chỉ hiện dấu ký khi đã gửi / đã duyệt tương ứng.
 */
export const buildSignatureSlots = (data) => {
  const steps = Array.isArray(data?.approval_steps) ? data.approval_steps : [];
  const isSubmitted = Boolean(data?.status && data.status !== "draft");
  const documentCode = data?.request_code || data?.payment_code || "";
  const requesterSignedAt =
    steps.find((s) => s.created_at)?.created_at ||
    data?.updated_at ||
    data?.created_at ||
    null;

  const slots = [
    {
      key: "requester",
      title: "Người đề nghị",
      name: data?.employee_name || "—",
      signed: isSubmitted,
      signedAt: isSubmitted ? requesterSignedAt : null,
      signatureSerial: isSubmitted
        ? formatSignatureSerial(data?.id, documentCode)
        : null,
    },
  ];

  const usedRoleKeys = new Set(["requester"]);

  steps.forEach((step, idx) => {
    const roleKey = step.role_key || `level_${step.level || idx + 1}`;
    usedRoleKeys.add(roleKey);
    slots.push({
      key: `step_${step.level || idx + 1}_${roleKey}`,
      title: stripEnglishRoleSuffix(step.role_name) || `Cấp ${step.level || idx + 1}`,
      name: step.approver_name || "—",
      signed: step.status === "approved",
      signedAt: step.action_at || null,
      signatureSerial:
        step.status === "approved"
          ? formatSignatureSerial(step.id, documentCode)
          : null,
    });
  });

  DEFAULT_APPROVER_SLOTS.forEach((def) => {
    if (usedRoleKeys.has(def.role_key)) return;
    slots.push({
      key: `pad_${def.role_key}`,
      title: def.title,
      name: null,
      signed: false,
      signedAt: null,
    });
    usedRoleKeys.add(def.role_key);
  });

  while (slots.length < 4) {
    slots.push({
      key: `pad_extra_${slots.length}`,
      title: "Phê duyệt",
      name: null,
      signed: false,
      signedAt: null,
    });
  }

  return slots;
};

export const SignatureBlock = ({ slot }) => (
  <div className="flex flex-col h-full min-h-[6.75rem] print:break-inside-avoid">
    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-700 mb-1.5 text-center leading-snug px-0.5">
      {slot.title}
    </p>

    {slot.signed && slot.name ? (
      <div
        className="flex-1 rounded border border-emerald-600 bg-white px-2 py-1.5 text-left min-h-[5.75rem]"
        style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}
      >
        <div className="flex items-center gap-1 text-emerald-700 mb-1">
          <ESignValidIcon />
          <span className="text-[7.5px] font-bold uppercase leading-none tracking-wide">
            Chữ ký số hợp lệ
          </span>
        </div>
        <p className="text-[8.5px] leading-snug text-slate-700 break-words">
          <span className="text-slate-500">Ký bởi:</span>{" "}
          <span className="font-semibold text-slate-900">{slot.name}</span>
        </p>
        {slot.signedAt ? (
          <p className="text-[8.5px] leading-snug text-slate-700 mt-0.5">
            <span className="text-slate-500">Ký ngày:</span>{" "}
            <span className="font-mono tabular-nums text-[8px] text-slate-800">
              {formatSignedAt(slot.signedAt)}
            </span>
          </p>
        ) : null}
        {slot.signatureSerial ? (
          <p className="text-[8px] leading-snug text-slate-700 mt-0.5">
            <span className="text-slate-500">Số hiệu CKS:</span>{" "}
            <span className="font-mono text-[7.5px] break-all text-slate-800">
              {slot.signatureSerial}
            </span>
          </p>
        ) : null}
      </div>
    ) : (
      <div className="flex-1 flex flex-col items-center justify-end rounded border border-dashed border-slate-300 bg-white px-2 py-2 min-h-[5.75rem] print:break-inside-avoid">
        <p className="text-[8.5px] italic text-slate-400">(Chưa ký)</p>
        <div className="mt-auto w-full border-b border-dotted border-slate-300 pt-6" />
      </div>
    )}
  </div>
);

/** Giấy phiếu tạm ứng dạng văn bản hành chính */
const AdvanceDocumentPaper = ({ data, lines = [] }) => {
  const status = STATUS_MAP[data?.status]?.label || data?.status;
  const signatureSlots = useMemo(() => buildSignatureSlots(data), [data]);

  return (
    <article className="bg-white text-slate-900 shadow-[0_12px_40px_rgba(15,23,42,0.12)] border border-slate-200/80 relative overflow-hidden print:shadow-none">
      <div className="absolute inset-y-0 left-0 w-1.5 bg-red-700" />
      <div className="px-6 py-7 md:px-8 md:py-9 space-y-5">
        <header className="text-center space-y-1 border-b border-slate-300 pb-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">
            Cộng hòa xã hội chủ nghĩa Việt Nam
          </p>
          <p className="text-[11px] font-semibold italic text-slate-500">
            Độc lập – Tự do – Hạnh phúc
          </p>
          <div className="mx-auto w-16 border-t border-slate-400 mt-2" />
          <h1 className="pt-3 text-lg md:text-xl font-black uppercase tracking-wide text-slate-900">
            Đề nghị tạm ứng
          </h1>
          <p className="text-xs text-slate-500">
            Số: <span className="font-bold text-slate-800">{data?.request_code || "—"}</span>
          </p>
        </header>

        <section className="space-y-2 text-[13px] leading-relaxed">
          <DocRow label="Họ và tên" value={data?.employee_name} />
          <DocRow
            label="Đơn vị"
            value={[data?.branch_name, data?.department_name].filter(Boolean).join(" — ")}
          />
          <DocRow label="Ngày đề nghị nhận tiền" value={formatDateVN(data?.request_date)} />
          <DocRow label="Ngày hoàn tạm ứng" value={formatDateVN(data?.repayment_due_date)} />
          <DocRow label="Hình thức nhận tiền" value={paymentMethodLabel(data?.payment_method)} />
          {(data?.bank_name || data?.account_number) && (
            <>
              <DocRow label="Ngân hàng" value={data.bank_name} />
              <DocRow label="Số tài khoản" value={data.account_number} mono />
              <DocRow label="Chủ tài khoản" value={data.account_holder} />
            </>
          )}
        </section>

        <section>
          <p className="text-[12px] font-bold uppercase tracking-wide text-slate-700 mb-2">
            Nội dung tạm ứng
          </p>
          <table className="w-full border border-slate-300 text-[12px]">
            <thead>
              <tr className="bg-slate-50">
                <th className="border border-slate-300 px-2 py-1.5 w-10 text-center font-bold">
                  STT
                </th>
                <th className="border border-slate-300 px-2 py-1.5 text-left font-bold">
                  Lý do / Nội dung
                </th>
                <th className="border border-slate-300 px-2 py-1.5 w-[28%] text-right font-bold">
                  Số tiền (₫)
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="border border-slate-300 px-2 py-3 text-center text-slate-400 italic"
                  >
                    {data?.advance_reason || "Chưa có nội dung"}
                  </td>
                </tr>
              ) : (
                lines.map((line, idx) => (
                  <tr key={line.id || idx}>
                    <td className="border border-slate-300 px-2 py-1.5 text-center align-top">
                      {line.line_no || idx + 1}
                    </td>
                    <td className="border border-slate-300 px-2 py-1.5 align-top">
                      {line.description || "—"}
                    </td>
                    <td className="border border-slate-300 px-2 py-1.5 text-right font-semibold tabular-nums align-top">
                      {Number(line.amount || 0).toLocaleString("vi-VN")}
                    </td>
                  </tr>
                ))
              )}
              <tr className="bg-slate-50">
                <td
                  colSpan={2}
                  className="border border-slate-300 px-2 py-2 text-right font-bold uppercase text-[11px]"
                >
                  Tổng cộng
                </td>
                <td className="border border-slate-300 px-2 py-2 text-right font-black text-red-700 tabular-nums">
                  {Number(data?.advance_amount || 0).toLocaleString("vi-VN")}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-[12px] text-slate-700 leading-relaxed">
            <span className="font-semibold">Số tiền bằng chữ:</span>{" "}
            <span className="italic">
              {amountToWords(data?.advance_amount) || "…………………………"}
            </span>
          </p>
        </section>

        <section className="grid grid-cols-2 gap-4 pt-2 text-[12px]">
          <div>
            <p className="font-bold text-slate-700 mb-1">Trạng thái phiếu</p>
            <p className="text-slate-600">{status}</p>
          </div>
          <div className="text-right">
            <p className="font-bold text-slate-700 mb-1">Ngày lập</p>
            <p className="text-slate-600">
              {formatDateVN(data?.created_at || data?.request_date)}
            </p>
          </div>
        </section>

        <section className="pt-4 border-t border-slate-200">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-4 text-center">
            Chữ ký số
          </p>
          <div
            className={`grid gap-4 ${
              signatureSlots.length <= 4
                ? "grid-cols-2 sm:grid-cols-4"
                : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
            }`}
          >
            {signatureSlots.map((slot) => (
              <SignatureBlock key={slot.key} slot={slot} />
            ))}
          </div>
        </section>
      </div>
    </article>
  );
};

export default AdvanceDocumentPaper;
